import axios from "axios";
import { isImportReport, normalizeImportReport } from "../../components/ImportReport/importReport";

import { toast } from "react-toastify";
import i18n from "../../i18n";
import { PDA_PREFIX } from "../../global/constants";
import logoutforSpecificErrorCodes from "../../securityUtils/LogoutUtils";
import { logout } from "../security-services/SecurityActions";
import { getActiveTenantHeader } from "../../global/tenant";
import { stripScenarioBreakdowns } from "./libraryBreakdowns";

import {
  DELETE_STRUCTURE,
  GET_STRUCTURE,
  GET_ERRORS,
  GET_STRUCTURES,
  IMPORT_CSV_STRUCTURE_SUCCESS,
  IMPORT_CSV_STRUCTURE_REQUEST,
  IMPORT_CSV_STRUCTURE_ERROR,
  GET_LIBRARIES_REQUEST,
  GET_LIBRARIES_SUCCESS,
  GET_LIBRARIES_ERROR,
  CREATE_LIBRARY_REQUEST,
  CREATE_LIBRARY_SUCCESS,
  CREATE_LIBRARY_ERROR,
  EDIT_LIBRARY_REQUEST,
  EDIT_LIBRARY_SUCCESS,
  EDIT_LIBRARY_ERROR,
  DUPLICATE_LIBRARY_REQUEST,
  DUPLICATE_LIBRARY_SUCCESS,
  DUPLICATE_LIBRARY_ERROR,
  DELETE_LIBRARY_REQUEST,
  DELETE_LIBRARY_SUCCESS,
  DELETE_LIBRARY_ERROR,
  DELETE_LIBRARIES_BULK_MARK_PENDING,
  DELETE_LIBRARIES_BULK_SUCCESS,
  DELETE_LIBRARIES_BULK_ERROR,
  GET_STRUCTURES_REQUEST,
  GET_STRUCTURES_SUCCESS,
  GET_STRUCTURES_ERROR,
  GET_LIBRARY_STRUCTURES_REQUEST,
  GET_LIBRARY_STRUCTURES_SUCCESS,
  GET_LIBRARY_STRUCTURES_ERROR,
  CREATE_STRUCTURES_REQUEST,
  CREATE_STRUCTURES_SUCCESS,
  CREATE_STRUCTURES_ERROR,
  UPDATE_STRUCTURE_SUCCESS,
  GET_LIBRARIES_WITH_CAPEX_REQUEST,
  GET_LIBRARIES_WITH_CAPEX_SUCCESS,
  GET_LIBRARIES_WITH_CAPEX_ERROR,
  DUPLICATE_STRUCTURE_REQUEST,
  DUPLICATE_STRUCTURE_SUCCESS,
  DUPLICATE_STRUCTURE_ERROR,
} from "../types";

const getLibrariesSuccess = (libraries) => ({
  type: GET_LIBRARIES_SUCCESS,
  payload: libraries,
});

const getStructuresRequest = () => ({ type: GET_STRUCTURES_REQUEST });

const getStructuresSuccess = (structures) => ({
  type: GET_STRUCTURES_SUCCESS,
  payload: structures,
});

const getStructuresError = (err) => ({
  type: GET_STRUCTURES_ERROR,
  payload: err,
});

export const createStructuresRequest = () => ({
  type: CREATE_STRUCTURES_REQUEST,
});

export const createStructuresSuccess = (structures) => ({
  type: CREATE_STRUCTURES_SUCCESS,
  payload: structures,
});

export const createStructuresError = (err) => ({
  type: CREATE_STRUCTURES_ERROR,
  payload: err,
});

export const updateStructureSuccess = (structure) => ({
  type: UPDATE_STRUCTURE_SUCCESS,
  payload: structure,
});

const importCSVRequest = () => ({ type: IMPORT_CSV_STRUCTURE_REQUEST });

export const importCSVSuccess = (structures) => ({
  type: IMPORT_CSV_STRUCTURE_SUCCESS,
  payload: structures,
});

export const importCSVError = (err) => ({
  type: IMPORT_CSV_STRUCTURE_ERROR,
  payload: err,
});

// eslint-disable-next-line
export const createOrUpdateStructure = (
  structure,
  navigate,
  action,
  closePopup = null
) => async (dispatch) => {
  try {
    const res = await axios.post(`${PDA_PREFIX}/structure`, structure);
    closePopup && closePopup();
    if (action === "new") {
      navigate(`/model-structure/${res.data.id}`);
    } else if (action === "edit") {
      dispatch(updateStructureSuccess(res.data));
    }

    dispatch({
      type: GET_ERRORS,
      payload: {},
    });
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }

    dispatch({
      type: GET_ERRORS,
      payload: err.response.data,
    });
    if (
      err.response.data?.code &&
      err.response.data?.code.includes("Structure Code")
    ) {
      toast.warn(
        <div>
          Scope code <b>{structure.codeStructureId}</b> already exists.
        </div>
      );
    } else {
      toast.error("Something went wrong");
    }
  }
};

export const importScopes = (
  projectId,
  scenarioId,
  structureId,
  scopeForm,
  navigate
) => async (dispatch) => {
  dispatch(createStructuresRequest());
  try {
    const res = await axios.post(
      `${PDA_PREFIX}/structure/import/${scenarioId}/${structureId}`,
      scopeForm
    );
    navigate(
      `/preparation/view/scope/${projectId}/${res.data.id}/${res.data.structureId}`
    );
    dispatch(createStructuresSuccess());
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(createStructuresError());
    toast.warning(err.response.data);
  }
};

export const importLibraryByCSV = (file) => async (dispatch) => {
  dispatch(importCSVRequest());
  try {
    const res = await axios.post(`${PDA_PREFIX}/structure/upload-csv`, file);
    // dispatch(importCSVSuccess(res.data)); // commented to fix bug
    // A newer backend answers with the CsvValidationReportDTO (counters +
    // warnings; a rejected file is HTTP 200 with valid=false). The legacy
    // endpoint answers with an empty body.
    const report = isImportReport(res.data) ? res.data : null;
    if (report && normalizeImportReport(report).valid === false) {
      dispatch(importCSVError());
      return { success: false, errorMessage: report };
    }
    toast.success(i18n.t("library.import.csvProcessed", "CSV file has been processed successfully."));
    return { success: true, report };
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(importCSVError());
    const data = err.response?.data;
    // Pass structured error object (with errors array) when present; otherwise extract a string
    const errorMessage =
      data && typeof data === "object" && Array.isArray(data.errors)
        ? data
        : data?.error || data?.message || data || "Something went wrong";
    return { success: false, errorMessage };
  }
};
/**
 * Synchronous XLSX import for the BreakdownStructure hierarchy on the
 * My Knowledge → Structures tab. Wraps the user-picked File in FormData
 * and POSTs to {@code /structure/xlsx-import} (library-mode — see
 * {@code BreakdownStructureExcelImportService}). On a 200 response the
 * BE returns {@code { createdBreakdownStructures, linkedScopes, warnings, failed }}.
 *
 * On a 400 the BE returns {@code { error }} — typically a fail-fast
 * "Scope(s) not found in library: …" message because the file
 * references a Library Structure that doesn't exist yet (per spec
 * §4: "Must NOT create Scopes").
 *
 * Returns {@code { success, data, error }} so the calling dialog can
 * render an inline result panel (warnings + failed are surfaced there,
 * not via individual toasts which get noisy past ~5 rows).
 */
export const importBreakdownStructuresFromExcel =
  (file) => async (dispatch, getState) => {
  const fd = new FormData();
  fd.append("file", file);
  try {
    // Multipart POSTs bypass api-gw's automatic X-tenant injection, so the
    // request reaches pda-be with no tenant and TenantRoutingDataSource falls
    // back to the DEFAULT datasource — another database, where the caller has
    // no user row. The import then failed on "no portfolio attached" for a user
    // whose row and portfolio are perfectly fine in their own tenant.
    const res = await axios.post(`${PDA_PREFIX}/structure/xlsx-import`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...getActiveTenantHeader(getState()),
      },
    });
    const data = res.data || {};
    const created = data.createdBreakdownStructures || [];
    const warnings = data.warnings || [];
    const summary =
      `${created.length} structure${created.length === 1 ? "" : "s"} created` +
      (warnings.length > 0
        ? `, ${warnings.length} skipped`
        : "");
    toast.success(summary);
    // The Structures tab is driven by getAllScopeGroups (BreakdownStructure list).
    const { getAllScopeGroups } = await import(
      "../group-services/groupsActions"
    );
    dispatch(getAllScopeGroups());
    return { success: true, data };
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    const data = err.response?.data;
    const errorMessage =
      data?.error || data?.message || err?.message || "Excel import failed";
    toast.error(errorMessage);
    return { success: false, data, error: errorMessage };
  }
};

/**
 * Downloads the empty 2-sheet .xlsx template (Structures + Scopes; columns
 * code/name/parent_code/description) a user can fill in and upload via
 * {@link importBreakdownStructuresFromExcel}.
 */
export const downloadStructureXlsxTemplate = async () => {
  const res = await axios.get(`${PDA_PREFIX}/structure/xlsx-template`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "structure_import_template.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Downloads the current portfolio's BS hierarchy + scope attachments —
 * same 2-sheet shape as the template, populated with real data (including
 * each scope's position/display_name under its parent) — so it can be
 * bulk-edited and re-imported via {@link importBreakdownStructuresFromExcel}.
 */
export const downloadStructureXlsxExport = async () => {
  const res = await axios.get(`${PDA_PREFIX}/structure/xlsx-export`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "structure_export.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox/Safari require the anchor to be in the DOM, and revoking the
  // object URL in the same tick can abort the in-flight download (same
  // fix as LibraryImportDialog.js's template download).
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const triggerCsvDownload = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Downloads the populated library CSV export for one structure (scope),
 * including the Block Code column. Used to fill the boundaries JSON.
 */
export const downloadStructureCsvExport = async (structureId) => {
  const res = await axios.get(
    `${PDA_PREFIX}/structure/${structureId}/csv-export`,
    { responseType: "blob" }
  );
  triggerCsvDownload(res.data, `library_export_${structureId}.csv`);
};

/**
 * Exports the whole knowledge library as a populated CSV (the "filled
 * template"), with the Block Code column. No id needed — scoped to the
 * caller's portfolio.
 *
 * Runs as a WebSocket job: a real library takes minutes to assemble and the
 * synchronous download timed out. `onProgress({ current, total, etaSeconds })`
 * is called per exported scope; the returned promise resolves once the file has
 * been fetched and handed to the browser.
 */
export const downloadLibraryCsvExport = async ({ onProgress } = {}) => {
  // Imported here, not at module scope: the WS service reaches the STOMP
  // client, which builds the Redux store — and this module is itself pulled in
  // from the store's own graph, so a static import closes the cycle and leaves
  // the root reducer undefined at load time.
  const { fetchLibraryCsvExport, newExportKey, startLibraryCsvExportWebSocket } =
    await import("./LibraryCsvExportWebSocketService");
  const exportKey = newExportKey();
  return new Promise((resolve, reject) => {
    startLibraryCsvExportWebSocket(exportKey, {
      onQueued: (frame) => onProgress?.({
        current: 0,
        total: frame.total,
        etaSeconds: frame.etaSecondsRemaining,
      }),
      onItemCompleted: (frame) => onProgress?.({
        current: frame.current,
        total: frame.total,
        etaSeconds: frame.etaSecondsRemaining,
      }),
      onAllCompleted: async (frame) => {
        try {
          // The frame describes the file; the bytes come over HTTP, because a
          // frame carrying a whole library's CSV would exceed the SockJS limit.
          const jobId = frame.result?.jobId ?? frame.jobId;
          const res = await fetchLibraryCsvExport(jobId);
          triggerCsvDownload(res.data, frame.result?.filename ?? "library_export.csv");
          resolve(frame.result);
        } catch (error) {
          reject(error);
        }
      },
      onAllFailed: (frame) => reject(new Error(frame?.error || "The library export failed.")),
      onRequestError: (error) => reject(error),
    });
  });
};

/**
 * Downloads the boundaries JSON template pre-filled with block_code and
 * block_name (historical_values empty). Ready to send to an LLM for
 * automatic filling, then import via POST /boundaries/import-json.
 */
export const downloadLibraryBoundariesTemplate = async () => {
  const res = await axios.get(
    `${PDA_PREFIX}/structure/library-boundaries-template`,
    { responseType: "blob" }
  );
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/json" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "boundaries_template.json");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getStructures = () => async (dispatch) => {
  dispatch(getStructuresRequest());
  try {
    const res = await axios.get(`${PDA_PREFIX}/structure/all`);
    dispatch(getLibrariesSuccess(res.data.libraries));
    dispatch(getStructuresSuccess(res.data.structures));
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
  }
};

export const getOrderedNonEmptyStructures = () => async (dispatch) => {
  dispatch(getStructuresRequest());
  try {
    const res = await axios.get(`${PDA_PREFIX}/structure/non-empty/all/ordered-by-name`);
    dispatch(getStructuresSuccess(res.data));
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(getStructuresError(err));
  }
};

export const getOrderedStructures = () => async (dispatch) => {
  dispatch(getStructuresRequest());
  try {
    const res = await axios.get(`${PDA_PREFIX}/structure/all/ordered-by-name`);
    dispatch(getStructuresSuccess(res.data));
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(getStructuresError(err));
  }
};

// Fetch the full library structure list into a DEDICATED slice
// (`state.structure.libraryStructures`). Other pages that drive the
// gantt dispatch getOrderedNonEmptyStructures() into the shared
// `structures` slice — using the same slice from the Generative
// Scheduling page caused the Scoping library to be silently filtered
// to only "non-empty" structures, hiding legitimate library templates
// the user expects to pick from. The dedicated slice keeps the two
// consumers independent so the gantt's filter no longer leaks into the
// Scoping library.
//
// Endpoint preference order:
//   1. `/structure/all/library-for-gw` — new endpoint, returns the
//      full library type set (DEFINITE+INDEFINITE+STANDARD+IMPORTED
//      +CONVERTIBLE) minus the Scenario library.
//   2. `/structure/all/ordered-by-name` — legacy fallback, narrower
//      type set (DEFINITE+INDEFINITE only).
//
// We try (1) first; if the backend hasn't been rebuilt with the new
// controller yet it'll 404 and we transparently fall back to (2) so
// the library still renders. Once the backend is updated, the user
// automatically gets the wider catalogue with no client redeploy.
//
// Either response is an entity graph, so every structure also carries
// the SCENARIO-scoped buckets generated runs attached to it.
// `stripScenarioBreakdowns` drops those before they reach redux — see
// `libraryBreakdowns.js`.
export const getLibraryStructures = () => async (dispatch) => {
  dispatch({ type: GET_LIBRARY_STRUCTURES_REQUEST });
  try {
    let res;
    try {
      res = await axios.get(`${PDA_PREFIX}/structure/all/library-for-gw`);
    } catch (err) {
      if (err?.response?.status === 404) {
        res = await axios.get(
          `${PDA_PREFIX}/structure/all/ordered-by-name`
        );
      } else {
        throw err;
      }
    }
    dispatch({
      type: GET_LIBRARY_STRUCTURES_SUCCESS,
      payload: stripScenarioBreakdowns(res.data),
    });
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: GET_LIBRARY_STRUCTURES_ERROR, payload: err });
  }
};

export const getStructuresByLibrary = () => async (dispatch) => {
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/structure/all/grouped-by-library`
    );
    dispatch({
      type: GET_STRUCTURES,
      payload: res.data,
    });
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
  }
};

export const getPortfolioLibraries = () => async (dispatch) => {
  dispatch({ type: GET_LIBRARIES_REQUEST });
  try {
    const res = await axios.get(`${PDA_PREFIX}/libraries`);
    dispatch({
      type: GET_LIBRARIES_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: GET_LIBRARIES_ERROR });
  }
};

export const createPortfolioLibrary = (request, navigate) => async (dispatch) => {
  dispatch({ type: CREATE_LIBRARY_REQUEST });
  try {
    const res = await axios.post(`${PDA_PREFIX}/libraries/add`, request);
    dispatch({
      type: CREATE_LIBRARY_SUCCESS,
      payload: res.data,
    });
    navigate && navigate(`/scopes/${res.data.id}/${res.data.name}`);
    toast.success(`${i18n.t("term.library")} has been created successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: CREATE_LIBRARY_ERROR });
    toast.error("Something went wrong");
  }
};

export const editPortfolioLibrary = (request) => async (dispatch) => {
  dispatch({ type: EDIT_LIBRARY_REQUEST });
  try {
    const res = await axios.put(`${PDA_PREFIX}/libraries`, request);
    dispatch({
      type: EDIT_LIBRARY_SUCCESS,
      payload: res.data,
    });
    toast.success(`${i18n.t("term.library")} has been edited successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: EDIT_LIBRARY_ERROR });
    toast.error("Something went wrong");
  }
};

export const deletePortfolioLibrary = (id) => async (dispatch) => {
  dispatch({ type: DELETE_LIBRARY_REQUEST });
  try {
    await axios.delete(`${PDA_PREFIX}/libraries/${id}`);
    dispatch({
      type: DELETE_LIBRARY_SUCCESS,
      payload: id,
    });
    toast.success(`${i18n.t("term.library")} has been deleted successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: DELETE_LIBRARY_ERROR });
    toast.error("Something went wrong");
  }
};

/**
 * Bulk-deletes multiple libraries in a single HTTP call. Optimistic:
 * the affected rows are flagged `_deleting` immediately so the UI greys
 * them out; on response, successful ids are removed and failed ids are
 * un-flagged plus a toast surfaces the per-row reason. The BE accepts a
 * raw `Long[]` body and returns `{ deleted: number[], failed: [{id, reason}] }`.
 */
export const deletePortfolioLibraries = (ids) => async (dispatch, getState) => {
  if (!Array.isArray(ids) || ids.length === 0) return;
  dispatch({ type: DELETE_LIBRARIES_BULK_MARK_PENDING, payload: ids });
  // Snapshot names BEFORE the request so failure toasts can name the
  // library even if the reducer has already mutated state.
  const idToName = new Map();
  try {
    const all = getState().structure?.libraries || [];
    all.forEach((l) => idToName.set(l.id, l.name));
  } catch {
    // selector shape unexpected — toasts will fall back to id.
  }
  try {
    const res = await axios.delete(`${PDA_PREFIX}/libraries`, { data: ids });
    const deleted = Array.isArray(res.data?.deleted) ? res.data.deleted : [];
    const failed = Array.isArray(res.data?.failed) ? res.data.failed : [];
    dispatch({
      type: DELETE_LIBRARIES_BULK_SUCCESS,
      payload: { deleted, failedIds: failed.map((f) => f.id) },
    });
    if (deleted.length > 0) {
      toast.success(
        `${deleted.length} ${i18n.t("term.library").toLowerCase()}${
          deleted.length === 1 ? "" : "s"
        } deleted.`
      );
    }
    failed.forEach((f) => {
      const name = idToName.get(f.id) || `#${f.id}`;
      toast.error(`"${name}": ${f.reason || "delete failed"}`);
    });
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: DELETE_LIBRARIES_BULK_ERROR, payload: ids });
    toast.error("Bulk delete failed.");
  }
};

export const transferLibraryOwnership = (libraryId, newOwnerId) => async (dispatch) => {
  dispatch({ type: EDIT_LIBRARY_REQUEST });
  try {
    const res = await axios.put(`${PDA_PREFIX}/libraries/${libraryId}/transfer-ownership?newOwnerId=${newOwnerId}`);
    dispatch({
      type: EDIT_LIBRARY_SUCCESS,
      payload: res.data,
    });
    toast.success(`${i18n.t("term.library")} ownership has been transferred successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: EDIT_LIBRARY_ERROR });
    toast.error("Something went wrong");
  }
};

export const duplicatePortfolioLibrary = (libraryId, scopes) => async (
  dispatch
) => {
  dispatch({ type: DUPLICATE_LIBRARY_REQUEST });
  try {
    const res = await axios.post(
      `${PDA_PREFIX}/libraries/${libraryId}`,
      scopes
    );
    dispatch({
      type: DUPLICATE_LIBRARY_SUCCESS,
      payload: res.data,
    });
    toast.success(`${i18n.t("term.library")} has been duplicated successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: DUPLICATE_LIBRARY_ERROR });
    toast.error("Something went wrong");
  }
};

export const getLibrariesWithCapex = () => async (dispatch) => {
  dispatch({ type: GET_LIBRARIES_WITH_CAPEX_REQUEST });
  try {
    const res = await axios.get(`${PDA_PREFIX}/libraries/with-capex`);
    dispatch({
      type: GET_LIBRARIES_WITH_CAPEX_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: GET_LIBRARIES_WITH_CAPEX_ERROR });
  }
};

export const getStructure = (id) => async (dispatch) => {
  const res = await axios.get(`${PDA_PREFIX}/structure/${id}`);
  dispatch({
    type: GET_STRUCTURE,
    payload: res.data,
  });
};

export const duplicateStructure = (id) => async (dispatch) => {
  dispatch({ type: DUPLICATE_STRUCTURE_REQUEST });
  try {
    const res = await axios.post(`${PDA_PREFIX}/structure/${id}`);
    dispatch({
      type: DUPLICATE_STRUCTURE_SUCCESS,
      payload: res.data,
    });
    toast.success(`${i18n.t("term.scope")} has been duplicated successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch({ type: DUPLICATE_STRUCTURE_ERROR });
    toast.error("Something went wrong");
  }
};

export const deleteStructure = (id) => async (dispatch) => {
  try {
    await axios.delete(`${PDA_PREFIX}/structure/${id}`);
    // navigate("/knowledge/model-structures");
    // navigate(`/scopes/${library}`);
    dispatch({
      type: DELETE_STRUCTURE,
      payload: id,
    });
    toast.success(`${i18n.t("term.scope")} has been deleted successfully.`);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    toast.error(
      "You cannot delete this scope as you currently use it in one of your project"
    );
  }
};

export const makeScopeConvertible = (id) => async (dispatch) => {
  try {
    const res = await axios.put(
      `${PDA_PREFIX}/structure/${id}/convert-to-convertible`
    );
    dispatch(updateStructureSuccess(res.data));
    toast.success(
      <div>
        <b>{res.data.name}</b> is now a convertible{" "}
        {i18n.t("term.scope").toLowerCase()}.
      </div>
    );
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    const data = err.response?.data;
    const backendMessage =
      typeof data === "string" ? data : data?.error || data?.message;
    toast.error(
      backendMessage ||
        "Something went wrong during conversion. Please contact support."
    );
  }
};

export const propagateInputOnScope = async (scopeId, inputId) => {
  const formData = new FormData();
  formData.append("attributeId", inputId);
  formData.append("structureId", scopeId);
  await axios.post(`${PDA_PREFIX}/block/propagate/attribute`, formData);
}

export const assignResourcesOnScope = async (scopeId, resourceIds) => {
  const formData = new FormData();
  formData.append("structureId", scopeId);
  formData.append("resourceIds", resourceIds);
  await axios.post(`${PDA_PREFIX}/activity/propagate/resource`, formData);
}

export const propagateTagsOnScopes = async (scopeId, tagIds) => {
  const formData = new FormData();
  formData.append("tagIds", tagIds);
  formData.append("structureId", scopeId);
  await axios.post(`${PDA_PREFIX}/block/propagate/tags`, formData);
}